import { Box, Flex, Strong } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { SidebarHelper } from 'classes/helpers/sidebar.helper';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonTextInput } from 'components/common/form/text';
import { t } from 'i18next';
import { DEFAULT_ACCEPT_BTN } from 'interfaces/i-dialogs';
import { ISidebarFolder } from 'interfaces/i-sidebar';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useState } from 'react';
import useListLibraryStore from 'stores/use-list-library-store';

const COMPONENT_NAME = 'RenameFolderDialog';

interface IProps {
  folder: ISidebarFolder;
  onClose: () => void;
}

export const RenameFolderDialog = (props: IProps) => {
  const loading = useListLibraryStore((s) => s.loading);
  const [folder, setFolder] = useState(props.folder.pathDisplay);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.MD}
        title={t('common.rename-x', { x: t('common.folder') })}
        loading={loading}
        content={
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <Box>
              <p>
                <Strong>Warning:</Strong> This will affect{' '}
                <Strong>all lists</Strong> in this folder.
              </p>
              <p>
                Current Folder/Path:{' '}
                <Strong>{props.folder.pathDisplay || '(none)'}</Strong>
              </p>
            </Box>

            <CommonFormGrid columns={1}>
              <Box>
                <CommonTextInput
                  id="rename-folder-folder"
                  placeholder="Type in a new folder or path"
                  value={folder}
                  onChange={(v) => setFolder(v ?? '')}
                />
              </Box>
            </CommonFormGrid>
          </Flex>
        }
        buttons={[
          {
            ...DEFAULT_ACCEPT_BTN,
            onClick: async () => {
              if (folder.length === 0) {
                NotifyHelper.error({
                  message_md:
                    'Please provide a non-empty name for your folder.',
                });
                return;
              }

              if (
                ArrayHelper.equals(
                  SidebarHelper.parseFolder(folder),
                  props.folder.pathComponents
                )
              ) {
                NotifyHelper.info({
                  message_md: 'Folder name was not changed.',
                });
                props.onClose();
                return;
              }

              /** change all folders for pitch lists within the same folder */
              const success = await useListLibraryStore
                .getState()
                .renameFolder({
                  reference_type: props.folder.reference_type,
                  _parent_id: props.folder._parent_id,
                  fromFolder: props.folder.pathDisplay,
                  toFolder: folder,
                });

              if (success) {
                props.onClose();
              }
            },
          },
        ]}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
